/**
 * AI Product FAQ Widget
 * Vanilla JS, no dependencies. Talks to your own Cloudflare Worker backend.
 *
 * 2026-07-26 22:12 MST
 * Changes vs previous build:
 *
 * 1. CACHE POISONING FIX. This is what killed the pills on the pre-order
 *    survey page. loadInitialFaqs() treated ANY cached object as a hit,
 *    and writeCache() stored every successful response including empty
 *    ones. The first load on a page whose FAQ source was still empty
 *    wrote {faqs:[],moreFaqs:[]} into localStorage, and from then on the
 *    widget short-circuited on that cached empty object and never called
 *    /generate-faqs again. Nothing rendered, and nothing hit the worker,
 *    which is why the request never showed up in the Cloudflare logs.
 *
 *    Two guards now: hasFaqs() must pass before a cached object is used,
 *    and again before anything is written to the cache. An empty result
 *    is now a cache miss, so the next page load retries the worker.
 *
 * 2. CACHE_VERSION bumped v4 -> v5. Necessary, not cosmetic. Without it,
 *    every browser that already stored an empty payload under the v4 key
 *    keeps reading it, since the new read guard only helps once the key
 *    changes or the entry is manually cleared.
 *
 * Nothing else in the file changed.
 */
(function () {
  "use strict";

  document.addEventListener("DOMContentLoaded", init);

  function init() {
    const root = document.getElementById("ai-faq-widget");
    if (!root) return;

    const workerUrl = root.dataset.workerUrl;
    if (!workerUrl) {
      console.warn("AI FAQ widget: no worker URL set on the block.");
      return;
    }

    const productId          = root.dataset.productId;
    const productTitle       = root.dataset.productTitle;
    const productDescription = root.dataset.productDescription;
    const productPrice       = root.dataset.productPrice;
    const productUrl         = root.dataset.productUrl;
    const lineupContext      = root.dataset.lineupContext || "";

    const streamEl          = document.getElementById("ai-faq-stream");
    const skeletonEl        = document.getElementById("ai-faq-skeleton");
    const suggestionsEl     = document.getElementById("ai-faq-suggestions");
    const askForm           = document.getElementById("ai-faq-ask-form");
    const askInput          = document.getElementById("ai-faq-ask-input");
    const askSubmit         = document.getElementById("ai-faq-ask-submit");
    const handoffEl         = document.getElementById("ai-faq-handoff");
    const handoffMessageEl  = document.getElementById("ai-faq-handoff-message");
    const questionDisplay   = document.getElementById("ai-faq-question-display");
    const emailInput        = document.getElementById("ai-faq-email");
    const submitTicketBtn   = document.getElementById("ai-faq-submit-ticket");
    const feedbackEl        = document.getElementById("ai-faq-form-feedback");

    // Bail if any critical element is missing so we don't crash silently mid-init
    if (!streamEl || !skeletonEl || !suggestionsEl || !askForm || !askInput || !askSubmit) {
      console.warn("AI FAQ widget: one or more required elements not found in the DOM.");
      return;
    }

    // Header with chat icon and title
    const headerDiv = document.createElement("div");
    headerDiv.className = "ai-faq-header";

    const chatIcon = document.createElement("img");
    chatIcon.src = "https://cdn.shopify.com/s/files/1/2455/7929/files/chaticon.png?v=1783458156";
    chatIcon.alt = "Chat";
    chatIcon.className = "ai-faq-header-icon";

    const headerTitle = document.createElement("h3");
    headerTitle.className = "ai-faq-header-title";
    headerTitle.textContent = "Ask Atlas AI";

    headerDiv.appendChild(chatIcon);
    headerDiv.appendChild(headerTitle);

    // Insert header before the skeleton/suggestions
    const containerFirstChild = root.firstChild;
    if (containerFirstChild) {
      root.insertBefore(headerDiv, containerFirstChild);
    } else {
      root.appendChild(headerDiv);
    }

    // Loading dots container (shown while FAQs load)
    const loadingDotsEl = document.createElement("div");
    loadingDotsEl.id = "ai-faq-loading-dots";
    loadingDotsEl.className = "ai-faq-loading-dots";
    skeletonEl.parentNode.insertBefore(loadingDotsEl, skeletonEl);

    // Create the three dot elements
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement("div");
      dot.className = "ai-faq-loading-dot";
      loadingDotsEl.appendChild(dot);
    }

    // Hide skeleton text but keep the loading dots visible
    skeletonEl.style.display = "none";
    skeletonEl.textContent = "";

    // "Talk to Allan" button injected below the ask form
    const talkToAllanBtn = document.createElement("button");
    talkToAllanBtn.type = "button";
    talkToAllanBtn.id = "ai-faq-talk-to-allan";
    talkToAllanBtn.className = "talk-to-allan-btn";

    // Create avatar image
    const allanAvatar = document.createElement("img");
    allanAvatar.src = "https://cdn.shopify.com/s/files/1/2455/7929/files/ATLAS_LIFESTYLE_MOAB_2018-24_1548988f-0568-46b4-9e90-670db6c75e3d.jpg?v=1783456267";
    allanAvatar.alt = "Allan";
    allanAvatar.className = "talk-to-allan-avatar";

    // Create text span
    const allanText = document.createElement("span");
    allanText.textContent = "Talk to Allan, the founder \u2192";

    talkToAllanBtn.appendChild(allanAvatar);
    talkToAllanBtn.appendChild(allanText);

    // Confirmation bubble sits between the ask form and the Talk to Allan button
    const confirmBubble = document.createElement("div");
    confirmBubble.id = "ai-faq-confirm";
    confirmBubble.className = "ai-faq-confirm-bubble";
    confirmBubble.style.display = "none";
    askForm.insertAdjacentElement("afterend", confirmBubble);

    const talkToAllanWrapper = document.createElement("div");
    talkToAllanWrapper.className = "ai-faq-talk-to-founder";
    talkToAllanWrapper.appendChild(talkToAllanBtn);
    confirmBubble.insertAdjacentElement("afterend", talkToAllanWrapper);

    const AI_ESCALATION_MESSAGE = "Atlas AI does not have the answers. Do you want to ask Allan?";
    const DIRECT_MESSAGE = "Got a question for Allan, the founder? Ask below and he'll follow up personally.";

    // Bumped v4 -> v5 on 2026-07-26 to invalidate every cached empty
    // payload written by the previous build. See header note 2.
    const CACHE_VERSION = "v5";
    const cacheKey = "ai-faq-cache-" + CACHE_VERSION + "-" + productId;
    const conversationHistory = [];
    let isDirectRequest = false;

    // Testing flag. Set localStorage.setItem("atlas_test", "1") in the
    // console before testing to tag every request from this browser as
    // test traffic, so it can be filtered out of the sheet log and never
    // creates a real Re:amaze ticket. Run
    // localStorage.removeItem("atlas_test") to turn it back off.
    let isTest = false;
    try {
      isTest = window.localStorage.getItem("atlas_test") === "1";
    } catch (e) {
      // localStorage unavailable (private mode, etc). Default to false.
      isTest = false;
    }
    if (isTest) {
      console.log("Atlas AI FAQ widget: test mode is ON (localStorage atlas_test=1). Requests are tagged as test traffic.");
    }

    // Generate a session ID once per page load to tie all questions together
    const sessionId = (isTest ? "test-" : "sess-") + Date.now() + "-" + Math.random().toString(36).slice(2, 8);

    let pendingMoreFaqs = [];
    let activePillCount = 0;
    let moreBtnEl = null;

    loadInitialFaqs();

    askForm.addEventListener("submit", function (e) {
      e.preventDefault();
      const question = askInput.value.trim();
      if (!question) return;
      askInput.value = "";
      handleCustomQuestion(question);
    });

    if (submitTicketBtn) submitTicketBtn.addEventListener("click", submitTicket);

    talkToAllanBtn.addEventListener("click", function () {
      isDirectRequest = true;
      if (handoffMessageEl) handoffMessageEl.textContent = DIRECT_MESSAGE;
      const lastQuestion =
        conversationHistory.length > 0
          ? conversationHistory[conversationHistory.length - 1].question
          : "";
      if (questionDisplay) {
        questionDisplay.value = lastQuestion;
        questionDisplay.removeAttribute("readonly");
        questionDisplay.placeholder = "Add any additional detail here...";
      }
      if (feedbackEl) feedbackEl.style.display = "none";
      if (handoffEl) handoffEl.style.display = "block";
      if (questionDisplay) {
        questionDisplay.focus();
        if (lastQuestion) {
          questionDisplay.setSelectionRange(lastQuestion.length, lastQuestion.length);
        }
      }
    });

    // ── Initial FAQ load ───────────────────────────────────────────────────

    // A payload with no questions in it is not a usable result. Treating one
    // as a cache hit is what silently broke non-product pages: the widget
    // rendered nothing and stopped calling the worker entirely.
    function hasFaqs(data) {
      if (!data) return false;
      var a = Array.isArray(data.faqs)     ? data.faqs.length     : 0;
      var b = Array.isArray(data.moreFaqs) ? data.moreFaqs.length : 0;
      return (a + b) > 0;
    }

    function loadInitialFaqs() {
      const cached = readCache();
      if (hasFaqs(cached)) {
        renderInitialFaqs(cached);
        return;
      }

      // Show loading dots while fetching from Claude
      loadingDotsEl.style.display = "flex";

      fetch(workerUrl + "/generate-faqs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: productUrl,
          title: productTitle,
          description: productDescription,
          price: productPrice,
          isTest: isTest,
        }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Bad response: " + res.status);
          return res.json();
        })
        .then(function (data) {
          console.log("Atlas AI FAQ response:", data);
          // Only cache a result that actually has questions in it, so an
          // empty response never becomes sticky for this browser.
          if (hasFaqs(data)) {
            writeCache(data);
          } else {
            console.warn("Atlas AI FAQ: worker returned no questions, not caching.");
          }
          renderInitialFaqs(data);
        })
        .catch(function (err) {
          console.error("Atlas AI failed to load FAQs", err);
          loadingDotsEl.style.display = "none";
          skeletonEl.style.display = "block";
          skeletonEl.textContent = "Couldn't load AI answers right now.";
        });
    }

    function shuffle(arr) {
      var a = arr.slice();
      for (var i = a.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = a[i];
        a[i] = a[j];
        a[j] = tmp;
      }
      return a;
    }

    function renderInitialFaqs(data) {
      // Hide loading dots once pills are ready to render
      loadingDotsEl.style.display = "none";

      // Pool the full set (initial + held-back extras) and shuffle so the
      // 3 pills shown differ on every page load, even from cached data.
      var pool = shuffle((data.faqs || []).concat(Array.isArray(data.moreFaqs) ? data.moreFaqs : []));

      var faqs = pool.slice(0, 3);
      pendingMoreFaqs = pool.slice(3);

      if (faqs.length === 0) return;

      suggestionsEl.style.display = "flex";
      activePillCount = faqs.length;

      faqs.forEach(function (faq) {
        addPill(faq);
      });

      if (pendingMoreFaqs.length > 0) {
        renderMoreButton();
      }
    }

    // ── Pill helpers ───────────────────────────────────────────────────────

    function addPill(faq) {
      const pill = buildPillEl(faq);
      suggestionsEl.insertBefore(pill, moreBtnEl || null);
    }

    function buildPillEl(faq) {
      const pill = document.createElement("div");
      pill.className = "suggestion-pill";
      pill.setAttribute("role", "button");
      pill.setAttribute("tabindex", "0");
      pill.textContent = faq.question;

      pill.addEventListener("click", function () {
        openPill(faq, pill);
      });

      pill.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          pill.click();
        }
      });

      return pill;
    }

    function openPill(faq, pill) {
      // Swap the pill for its answer right where the pill was sitting,
      // so the order never changes and nothing jumps around, no matter
      // what order the pills get clicked in.
      const answerBlock = document.createElement("div");
      answerBlock.className = "faq-answer-block";

      const questionBubble = buildBubble("user", faq.question, false, "faq-question");
      questionBubble.setAttribute("role", "button");
      questionBubble.setAttribute("tabindex", "0");
      questionBubble.title = "Click to collapse";

      answerBlock.appendChild(questionBubble);
      answerBlock.appendChild(buildBubble("ai", faq.answer));
      pill.parentNode.insertBefore(answerBlock, pill);
      pill.remove();

      activePillCount = Math.max(0, activePillCount - 1);
      checkSuggestionsEmpty();

      // Clicking the question again collapses the answer back into a pill,
      // right back in the same spot.
      function collapse() {
        const freshPill = buildPillEl(faq);
        answerBlock.parentNode.insertBefore(freshPill, answerBlock);
        answerBlock.remove();
        activePillCount += 1;
      }
      questionBubble.addEventListener("click", collapse);
      questionBubble.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          collapse();
        }
      });

      // Log pill clicks to the worker so they appear in the sheet
      fetch(workerUrl + "/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: productUrl,
          title: productTitle,
          description: productDescription,
          price: productPrice,
          question: faq.question,
          lineupContext: lineupContext,
          sessionId: sessionId,
          isTest: isTest,
          questionType: "pill",
          prefetchedAnswer: faq.answer,
        }),
      }).catch(function () {}); // fire and forget, don't block the UI
    }

    function checkSuggestionsEmpty() {
      if (activePillCount === 0 && !moreBtnEl) {
        suggestionsEl.style.display = "none";
        // If there are no stored extras, offer to generate more via Claude
        if (pendingMoreFaqs.length === 0) {
          renderMoreButton();
        }
      }
    }

    // ── More questions button ──────────────────────────────────────────────

    function renderMoreButton() {
      if (moreBtnEl) return;

      moreBtnEl = document.createElement("button");
      moreBtnEl.type = "button";
      moreBtnEl.className = "ai-faq-more-btn";
      moreBtnEl.textContent = "More questions \u2193";

      moreBtnEl.addEventListener("click", function () {
        moreBtnEl.remove();
        moreBtnEl = null;

        if (pendingMoreFaqs.length > 0) {
          // Reveal next stored batch
          var nextBatch = pendingMoreFaqs.splice(0, 3);
          activePillCount += nextBatch.length;
          suggestionsEl.style.display = "flex";
          nextBatch.forEach(function (faq) { addPill(faq); });
          if (pendingMoreFaqs.length > 0) renderMoreButton();
        } else {
          // No stored extras, ask Claude to generate more
          var loadingPill = document.createElement("div");
          loadingPill.className = "suggestion-pill bubble-ai loading";
          loadingPill.textContent = "Finding more questions...";
          suggestionsEl.style.display = "flex";
          suggestionsEl.appendChild(loadingPill);

          fetch(workerUrl + "/generate-faqs", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              url: productUrl,
              title: productTitle,
              description: productDescription,
              price: productPrice,
              offset: true,
              isTest: isTest,
            }),
          })
            .then(function (res) {
              if (!res.ok) throw new Error("Bad response: " + res.status);
              return res.json();
            })
            .then(function (data) {
              loadingPill.remove();
              var more = (data.faqs || []).concat(data.moreFaqs || []);
              if (more.length === 0) {
                var noneEl = document.createElement("p");
                noneEl.style.fontSize = "14px";
                noneEl.style.color = "#6b7280";
                noneEl.textContent = "No more questions to show.";
                suggestionsEl.appendChild(noneEl);
                return;
              }
              activePillCount += more.length;
              more.forEach(function (faq) { addPill(faq); });
            })
            .catch(function () {
              loadingPill.remove();
            });
        }
      });

      suggestionsEl.style.display = "flex";
      suggestionsEl.appendChild(moreBtnEl);
    }

    // ── Custom question ────────────────────────────────────────────────────

    function handleCustomQuestion(question) {
      const qaBlock = document.createElement("div");
      qaBlock.className = "faq-answer-block";
      streamEl.appendChild(qaBlock);

      qaBlock.appendChild(buildBubble("user", question));
      const loadingBubble = buildBubble("ai", "Thinking...", true);
      qaBlock.appendChild(loadingBubble);
      askSubmit.disabled = true;

      fetch(workerUrl + "/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: productUrl,
          title: productTitle,
          description: productDescription,
          price: productPrice,
          question: question,
          lineupContext: lineupContext,
          sessionId: sessionId,
          isTest: isTest,
          questionType: "custom",
        }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Bad response: " + res.status);
          return res.json();
        })
        .then(function (data) {
          loadingBubble.classList.remove("loading");
          loadingBubble.querySelector("p").textContent = data.answer;
          askSubmit.disabled = false;

          // ── Product links ────────────────────────────────────────────────
          // Render one link per entry in product_links (array of {url, name}).
          // Only present when Claude identified one or more specific products
          // the answer is about, and none of them is the current page's product.
          // Falls back gracefully when the field is absent or empty.
          var links = Array.isArray(data.product_links) ? data.product_links : [];
          links.forEach(function (item) {
            if (!item || !item.url) return;
            var linkEl = document.createElement("a");
            linkEl.href = item.url;
            linkEl.textContent = "\u2192 Learn more about the " + (item.name || "product");
            linkEl.className = "ai-faq-product-link";
            qaBlock.appendChild(linkEl);
          });
          // ────────────────────────────────────────────────────────────────

          conversationHistory.push({
            question: question,
            answer: data.answer,
            confident: !!data.confident,
          });

          if (!data.confident) {
            isDirectRequest = false;
            if (handoffMessageEl) handoffMessageEl.textContent = AI_ESCALATION_MESSAGE;
            if (questionDisplay) {
              questionDisplay.value = question;
              questionDisplay.removeAttribute("readonly");
              questionDisplay.placeholder = "Add any additional detail here...";
            }
            if (handoffEl) handoffEl.style.display = "block";
          }
        })
        .catch(function (err) {
          console.error("AI FAQ widget: ask failed", err);
          loadingBubble.classList.remove("loading");
          loadingBubble.querySelector("p").textContent =
            "Oh oh. Atlas AI does not have the answer. Please ask Allan.";
          askSubmit.disabled = false;
          conversationHistory.push({
            question: question,
            answer: "(error: no answer returned)",
            confident: false,
          });
          isDirectRequest = false;
          if (handoffMessageEl) handoffMessageEl.textContent = AI_ESCALATION_MESSAGE;
          if (questionDisplay) {
            questionDisplay.value = question;
            questionDisplay.removeAttribute("readonly");
            questionDisplay.placeholder = "Add any additional detail here...";
          }
          if (handoffEl) handoffEl.style.display = "block";
        });
    }

    // ── Ticket submission ──────────────────────────────────────────────────

    function submitTicket() {
      const email    = emailInput ? emailInput.value.trim() : "";
      const question = questionDisplay ? questionDisplay.value.trim() : "";

      if (!email || !question) {
        showFeedback("Please enter your email and a question first.", "error");
        return;
      }

      submitTicketBtn.disabled = true;

      fetch(workerUrl + "/support-ticket", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email,
          question: question,
          history: conversationHistory,
          direct: isDirectRequest,
          product: productTitle,
          url: productUrl,
          sessionId: sessionId,
          isTest: isTest,
        }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Bad response: " + res.status);
          return res.json();
        })
        .then(function () {
          if (emailInput) emailInput.value = "";
          if (questionDisplay) questionDisplay.value = "";
          if (handoffEl) handoffEl.style.display = "none";
          confirmBubble.textContent = "Message sent. Allan will be notified and answer you back at " + email + ".";
          confirmBubble.style.display = "block";
          setTimeout(function () { confirmBubble.style.display = "none"; }, 6000);
          isDirectRequest = false;
        })
        .catch(function (err) {
          console.error("AI FAQ widget: ticket submission failed", err);
          showFeedback("Couldn't send that. Please try again.", "error");
        })
        .finally(function () {
          submitTicketBtn.disabled = false;
        });
    }

    // ── Shared helpers ─────────────────────────────────────────────────────

    function buildBubble(role, text, loading, variant) {
      const bubble = document.createElement("div");
      bubble.className =
        "chat-bubble " + (role === "user" ? "bubble-user" : "bubble-ai");
      if (loading) bubble.classList.add("loading");
      if (variant) bubble.classList.add(variant);
      const p = document.createElement("p");
      p.textContent = text;
      bubble.appendChild(p);
      return bubble;
    }

    function showFeedback(message, type) {
      if (!feedbackEl) return;
      feedbackEl.textContent = message;
      feedbackEl.className = type;
      feedbackEl.style.display = "block";
    }

    function readCache() {
      try {
        const raw = localStorage.getItem(cacheKey);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }

    function writeCache(data) {
      try {
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch (e) {
        // Ignore storage errors in private browsing
      }
    }
  }
})();